import { toPlainText } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import type { FAQPage, WithContext } from 'schema-dts';

import { faqBlockId, faqPageId, productFaqId } from './helpers/ids';

interface FaqEntry {
  question: string;
  answer: PortableTextBlock[];
}

interface BuildFaqPageInput {
  entries: FaqEntry[];
  pagePath: string;
  // 'page' for /faq itself, 'block' for FAQ blocks embedded in other
  // pages, 'product' for a product page's dedicated FAQ list. Drives
  // the @id fragment so a single page can carry a primary entity
  // (Product, etc.) and embedded FAQs without collision.
  variant: 'page' | 'block' | 'product';
}

export type FaqPageSchema = WithContext<FAQPage>;

const idForVariant = (
  variant: BuildFaqPageInput['variant'],
  pagePath: string,
): string => {
  if (variant === 'page') return faqPageId(pagePath);
  if (variant === 'product') return productFaqId(pagePath);
  return faqBlockId(pagePath);
};

export const buildFaqPage = ({
  entries,
  pagePath,
  variant,
}: BuildFaqPageInput): FaqPageSchema | null => {
  if (entries.length === 0) {
    return null;
  }

  const id = idForVariant(variant, pagePath);

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': id,
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: toPlainText(entry.answer),
      },
    })),
  };
};
